import React from 'react';
import { Folder, FolderOpen, Building2, Clock, CheckCircle2, Activity, Zap } from 'lucide-react';

interface WorkspaceContextIndicatorProps {
  workspaceId?: string;
  workspaceName?: string;
  isActive?: boolean;
  showStatus?: boolean;
  className?: string;
}

/**
 * Workspace context indicator with Color Kit styling
 * Shows when a workspace is active and provides visual context
 */
export const WorkspaceContextIndicator: React.FC<WorkspaceContextIndicatorProps> = ({
  workspaceId,
  workspaceName,
  isActive = true,
  showStatus = true,
  className = ''
}) => {
  if (!workspaceId && !workspaceName) {
    return null;
  }

  return (
    <div className={`
      inline-flex items-center gap-2 px-3 py-1.5 
      bg-accent/50 text-accent-foreground
      border border-accent/20 rounded-md
      text-xs font-medium
      ${className}
    `}>
      {/* Workspace icon with active state */}
      {isActive ? (
        <FolderOpen className="w-3 h-3 text-primary" />
      ) : (
        <Folder className="w-3 h-3 text-muted-foreground" />
      )}
      
      {/* Workspace name */}
      <span className="truncate max-w-[120px]">
        {workspaceName || `Workspace ${workspaceId?.slice(-8)}`}
      </span>
      
      {/* Status indicator */}
      {showStatus && isActive && (
        <CheckCircle2 className="w-3 h-3 text-primary" />
      )}
    </div>
  );
};

/**
 * Enhanced workspace status badge for results sections with real-time connection support
 */
export const WorkspaceStatusBadge: React.FC<{
  workspaceName: string;
  resultCount?: number;
  isProcessing?: boolean;
  connectionStatus?: 'idle' | 'connected' | 'disconnected';
  // Legacy support for pollingStatus
  pollingStatus?: 'idle' | 'active' | 'completed';
  className?: string;
}> = ({ workspaceName, resultCount, isProcessing = false, connectionStatus, pollingStatus, className = '' }) => {
  
  // Determine the status display based on connection state or legacy polling state
  const getStatusDisplay = () => {
    if (isProcessing) {
      return (
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3 animate-pulse text-accent-foreground" />
          <span>Processing...</span>
        </div>
      );
    }

    // Show real-time connection status in development
    if (process.env.NODE_ENV === 'development') {
      // Handle new connectionStatus
      if (connectionStatus === 'connected') {
        return (
          <div className="flex items-center gap-1">
            <Activity className="w-3 h-3 animate-pulse text-green-500" />
            <span className="text-green-600">Real-time</span>
          </div>
        );
      }

      if (connectionStatus === 'disconnected') {
        return (
          <div className="flex items-center gap-1">
            <Activity className="w-3 h-3 text-red-500" />
            <span className="text-red-600">Disconnected</span>
          </div>
        );
      }

      // Legacy polling status support
      if (pollingStatus === 'active') {
        return (
          <div className="flex items-center gap-1">
            <Activity className="w-3 h-3 animate-pulse text-blue-500" />
            <span className="text-blue-600">Smart Polling</span>
          </div>
        );
      }

      if (pollingStatus === 'completed') {
        return (
          <div className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-green-500" />
            <span className="text-green-600">Complete</span>
          </div>
        );
      }
    }

    if (resultCount !== undefined) {
      return (
        <span className="
          px-1.5 py-0.5 bg-primary/10 text-primary 
          rounded text-xs font-medium
        ">
          {resultCount} results
        </span>
      );
    }

    return null;
  };

  return (
    <div className={`
      inline-flex items-center gap-2 px-2 py-1
      bg-card border border-border rounded
      text-xs text-muted-foreground
      ${className}
    `}>
      <Building2 className="w-3 h-3" />
      <span className="font-medium">{workspaceName}</span>
      
      {getStatusDisplay()}
    </div>
  );
};

/**
 * Enhanced workspace header for results display with real-time connection status
 */
export const WorkspaceResultsHeader: React.FC<{
  keyword: string;
  workspaceName?: string;
  language: string;
  location: string;
  resultCount: number;
  dataType: string;
  connectionStatus?: 'idle' | 'connected' | 'disconnected';
  // Legacy support for pollingStatus
  pollingStatus?: 'idle' | 'active' | 'completed';
  className?: string;
}> = ({ 
  keyword, 
  workspaceName, 
  language, 
  location, 
  resultCount, 
  dataType,
  connectionStatus,
  pollingStatus,
  className = '' 
}) => (
  <div className={`space-y-3 ${className}`}>
    {/* Main header with Color Kit typography */}
    <div>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          {resultCount} {dataType} results for "{keyword}"
        </h3>
        
        {/* Show connection status indicators in development UI */}
        {process.env.NODE_ENV === 'development' && (
          <>
            {connectionStatus === 'connected' && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-50 dark:bg-green-950/20 rounded-md">
                <Activity className="w-4 h-4 text-green-500 animate-pulse" />
                <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                  Real-time Connected
                </span>
              </div>
            )}
            
            {connectionStatus === 'disconnected' && (
              <div className="flex items-center gap-2 px-3 py-1 bg-red-50 dark:bg-red-950/20 rounded-md">
                <Activity className="w-4 h-4 text-red-500" />
                <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                  Connection Lost
                </span>
              </div>
            )}

            {/* Legacy polling status support */}
            {!connectionStatus && pollingStatus === 'active' && (
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-950/20 rounded-md">
                <Zap className="w-4 h-4 text-blue-500 animate-pulse" />
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  Smart Polling Active
                </span>
              </div>
            )}
            
            {!connectionStatus && pollingStatus === 'completed' && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-50 dark:bg-green-950/20 rounded-md">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                  Polling Complete
                </span>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Metadata with muted text */}
      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
        <span>{language} • {location}</span>
        
        {/* Workspace context if available */}
        {workspaceName && (
          <>
            <span>•</span>
            <WorkspaceContextIndicator
              workspaceName={workspaceName}
              isActive={true}
              showStatus={false}
              className="bg-accent/30 text-accent-foreground border-accent/30"
            />
          </>
        )}
      </div>
    </div>
    
    {/* Visual separator with Color Kit border */}
    <div className="border-t border-border"></div>
  </div>
);

/**
 * Workspace creation success message
 */
export const WorkspaceCreationSuccess: React.FC<{
  workspaceName: string;
  onViewWorkspace?: () => void;
  className?: string;
}> = ({ workspaceName, onViewWorkspace, className = '' }) => (
  <div className={`
    p-4 bg-card border border-border rounded-lg
    shadow-sm
    ${className}
  `}>
    <div className="flex items-start gap-3">
      <div className="p-2 bg-primary/10 rounded-full">
        <CheckCircle2 className="w-5 h-5 text-primary" />
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-foreground">
          Workspace Created Successfully
        </h4>
        <p className="text-sm text-muted-foreground mt-1">
          "{workspaceName}" is ready for keyword research and analysis.
        </p>
        
        {onViewWorkspace && (
          <button
            onClick={onViewWorkspace}
            className="
              mt-2 text-xs text-primary hover:text-primary/80
              font-medium underline underline-offset-2
            "
          >
            View Workspace
          </button>
        )}
      </div>
    </div>
  </div>
);

/**
 * Workspace context provider for pages
 */
export const WorkspaceContextProvider: React.FC<{
  children: React.ReactNode;
  workspaceId?: string;
  workspaceName?: string;
}> = ({ children, workspaceId, workspaceName }) => {
  return (
    <div className="space-y-4">
      {/* Context header if workspace is active */}
      {(workspaceId || workspaceName) && (
        <div className="
          px-4 py-2 bg-accent/20 border border-accent/30 rounded-md
          flex items-center gap-2
        ">
          <Building2 className="w-4 h-4 text-accent-foreground" />
          <span className="text-sm font-medium text-accent-foreground">
            Active Workspace: {workspaceName || `Workspace ${workspaceId?.slice(-8)}`}
          </span>
        </div>
      )}
      
      {children}
    </div>
  );
};